import { Grid } from '@mui/material';
import { CrossIcon, RoutalInputText, RoutalPalette } from '@nx-smartmonkey/ui';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { ResultType } from 'react-hook-speech-to-text';

import MicrophoneIcon from '../../assets/svg/MicrophoneIcon';
import { EventTrackTypes, SearchByVoiceSource } from '../../constants/EventTracks';
import { intl } from '../../helpers/IntGlobalProvider';
import { useCaptureEvents } from '../../hooks/useCaptureEvents';
import { useShowSnackbarMessage } from '../../hooks/useShowSnackbarMessage';
import { RecordingModal } from './RecordingModal';

type SearchInputProps = {
  width?: string | number;
  initialValue?: string;
  searchLive?: boolean;
  disabled?: boolean; //Disable input
  clearButton?: boolean; //button for clear the input
  voiceInput?: boolean;
  continuosVoiceInput?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  disableMicrophoneIfText?: boolean;
  source: SearchByVoiceSource;

  inputStyles?: React.CSSProperties;
  inputContainerStyles?: React.CSSProperties;
  onTextChange?: (arg0: string) => void;
};

export const SearchInput = ({
  clearButton,
  voiceInput,
  continuosVoiceInput, // TODO Creo que ya no funciona con el refactor, pero no se usaba
  initialValue,
  autoFocus,
  searchLive,
  disabled,
  placeholder,
  disableMicrophoneIfText,
  source,
  inputStyles,
  inputContainerStyles,
  onTextChange,
}: SearchInputProps) => {
  const [text, setText] = useState<string>(initialValue || ``);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState<boolean>(false);
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { capture } = useCaptureEvents();

  const handleChange = (value: string) => {
    const newText = value;
    setText(newText);
    if (onTextChange !== undefined && searchLive) {
      onTextChange(newText);
    }
  };

  useEffect(() => {
    if (onTextChange !== undefined && searchLive) {
      onTextChange(text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  useEffect(() => {
    setText(initialValue || ``);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  const handleClear = () => {
    setText(``);
    if (onTextChange !== undefined) onTextChange(``);
  };

  //Check if the browser has the permission to use the microphone
  const checkAudio = useCallback(async () => {
    // Get Audio Permission
    const handleCheckAudioEnabled = async () => {
      try {
        return await navigator.mediaDevices
          .getUserMedia({
            audio: true,
          })
          .then((media) => {
            return media.active;
          });
      } catch (err) {
        return false;
      }
    };
    const audioStatus = await handleCheckAudioEnabled();
    if (!audioStatus) {
      showSnackbarMessage({ type: `error`, messageIntlId: `driver.stop.search.voice.no_microphone` });
      setIsAudioEnabled(false);
    } else {
      setIsAudioEnabled(true);
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGetResults = (results: ResultType[] | string[]) => {
    if (results.length > 0) {
      const newText = (results as ResultType[]).at(-1)?.transcript;
      if (newText === undefined) return;
      setText(`${text} ${newText}`);
    }

    if (onTextChange !== undefined) {
      onTextChange(text);
    }
  };

  return (
    <>
      <RecordingModal
        continuosVoiceInput={continuosVoiceInput}
        showModal={showModal && isAudioEnabled}
        onStartRecording={() => {
          setText(``);
          setIsRecording(true);
        }}
        onStopRecording={(results: ResultType[] | string[]) => {
          onGetResults(results);
          setIsRecording(false);
          setShowModal(false);
        }}
        changeText={(results: ResultType[] | string[]) => {
          onGetResults(results);
        }}
      />
      <RoutalInputText
        placeholder={placeholder || intl.formatMessage({ id: `driver.stop_list.input_search_ph` })}
        fieldValue={text}
        size="medium"
        autoFocus={autoFocus}
        fullWidth
        hoverBGColor="transparent"
        disabled={isRecording || disabled}
        inputStyles={{
          borderRadius: `0px`,
          border: `0px solid ${RoutalPalette.grey7}`,
          height: `40px`,
          width: `100%`,
          ...inputStyles,
        }}
        inputContainerStyles={{
          borderRadius: `0px`,
          border: `0px solid ${RoutalPalette.grey7}`,
          ...inputContainerStyles,
        }}
        endAdornment={
          <Grid item container direction="row" wrap="nowrap" alignItems="end" style={{ width: `auto` }}>
            {text !== `` && clearButton ? (
              <Grid
                item
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                style={{
                  width: `40px`,
                  height: `40px`,
                }}
                onClick={() => handleClear()}
              >
                <CrossIcon style={{ color: RoutalPalette.secondary40, fontSize: `14px`, cursor: `pointer` }} />
              </Grid>
            ) : null}
            {voiceInput && (text === `` || !disableMicrophoneIfText) && (
              <Grid
                item
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                style={{
                  width: `54px`,
                  height: `54px`,
                }}
                onClick={() => {
                  if (!disabled) {
                    capture({
                      type: EventTrackTypes.SearchByVoice,
                      payload: {
                        [`From`]: source,
                      },
                    });
                    if (isAudioEnabled) {
                      setShowModal(true);
                    } else {
                      checkAudio();
                    }
                  }
                }}
              >
                {isRecording ? (
                  <motion.div
                    animate={{ opacity: [1, 0.2, 1], scale: [1, 0.9, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    style={{ display: `flex`, justifyContent: `center`, alignItems: `center`, height: `20px` }}
                  >
                    <MicrophoneIcon style={{ fill: RoutalPalette.secondary40, height: `20px` }} />
                  </motion.div>
                ) : (
                  <div style={{ display: `flex`, justifyContent: `center`, alignItems: `center`, height: `20px` }}>
                    {text === `` ? (
                      <MicrophoneIcon style={{ fill: RoutalPalette.grey1, height: `20px` }} />
                    ) : (
                      !disableMicrophoneIfText && (
                        <MicrophoneIcon style={{ fill: RoutalPalette.grey1, height: `20px` }} />
                      )
                    )}
                  </div>
                )}
              </Grid>
            )}
          </Grid>
        }
        onChange={(text) => handleChange(text)}
      />
    </>
  );
};
